import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import * as CryptoJS from 'crypto-js';


export enum StorageType {
   Local,
   Session
}

//* Todas las posibles claves a almacenar en el storage se deben definir aquí.
export const STORAGE_KEYS = {
   LISTADOS_PAGINACION: 'listados_paginacion',
   DARK_MODE_ENABLED: 'darkModeEnabled',
   USUARIO: 'usuario',
   ACCESS_TOKEN: 'access_token',
   SGIINDICE: 'sgiindice',
   CADUCIDAD_TOKEN: 'caducidadToken',
   CESTA: 'cesta_borrador',
   /** Paso del wizard de inscripción en el que se quedó el usuario (se restaura tras F5). */
   CESTA_PASO: 'cesta_paso',
} as const;

// Interfaz para almacenar el valor junto con su fecha de expiración (opcional)
interface StorageItem {
   value: any;
   expiresAt?: number; // Fecha de expiración en milisegundos desde la época (timestamp), opcional
}

// Interfaz para los mensajes de sincronización entre ventanas/pestañas
interface StorageSyncMessage {
   clientId: string;
   action: 'set' | 'remove' | 'clear';
   key?: string; // No es necesario para "clear"
   data?: StorageItem; // Útil en la acción "set"
}


@Injectable({
   providedIn: 'root'
})
export class StorageService {

   private readonly keyPrefix = 'SERGLO'; // Prefijo para las keys en el storage
   private readonly encryptionSecret = 'S3rgl0_Encr1pt10n_K3y_2026!'; // Clave para encriptar/desencriptar

   private readonly broadcastChannel: BroadcastChannel; // Instancia de BroadcastChannel para la sincronización entre pestañas
   private readonly clientId: string; // Identificador único para este contexto (pestaña o ventana)


   private storageSyncSubject = new Subject<StorageSyncMessage>(); // Subject para emitir eventos de sincronización de forma reactiva
   public storageChanges$: Observable<StorageSyncMessage> = this.storageSyncSubject.asObservable(); // Observable público al que se pueden suscribir otros componentes


   constructor() {
      this.broadcastChannel = new BroadcastChannel('SERGLOChannel'); // Inicializa el canal con un nombre único compartido entre las instancias de la aplicación
      this.clientId = Math.random().toString(36).substring(2);  // Genera un clientId aleatorio para identificar este contexto

      // Escucha los mensajes del canal
      this.broadcastChannel.onmessage = (event) => {
         const message: StorageSyncMessage = event.data;
         // Ignorar el mensaje si proviene de esta misma instancia
         if (message.clientId === this.clientId) {
            return;
         }
         // Aquí puedes manejar la sincronización, por ejemplo:
         // TODO: Procesar el mensaje recibido (ver como)
         // console.log('Mensaje recibido de otra pestaña:', message);
         // Dependiendo de la acción, podrías disparar algún Subject o refrescar un estado.
         // Por ejemplo actulizar modo oscuro si se recibe un mensaje de otra pestaña
      };
   }

   /**
 * Encripta una cadena de texto utilizando AES.
 * @param text Texto a encriptar.
 * @returns Texto encriptado en formato Base64.
 */
   private encrypt(text: string): string {
      try {
         // Usamos CryptoJS.AES para una encriptación estándar y segura
         const encrypted = CryptoJS.AES.encrypt(text, this.encryptionSecret);
         return encrypted.toString();
      } catch (error) {
         return text; // Si falla, devolvemos el texto original
      }
   }

   /**
    * Desencripta una cadena de texto encriptada previamente.
    * @param encryptedText Texto encriptado en formato Base64.
    * @returns Texto original desencriptado.
    */
   private decrypt(encryptedText: string): string {
      try {
         // Desencriptamos usando la misma clave secreta
         const decrypted = CryptoJS.AES.decrypt(encryptedText, this.encryptionSecret);
         return decrypted.toString(CryptoJS.enc.Utf8);
      } catch (error) {
         console.error('Error al desencriptar:', error);
         return encryptedText; // Si falla, devolvemos el texto encriptado
      }
   }


   /**
  * Guarda un valor serializado en JSON en el storage seleccionado. Envia mensaje de sincronización.
  * @param key Identificador sin prefijo.
  * @param value El dato a almacenar.
  * @param storageType [StorageType.Local] Tipo de almacenamiento (local o session). Por defecto, local.
  * @param expirationInMinutes (Opcional) Tiempo en minutos para que el ítem expire. Por defecto, no expira.
  * @param shouldEncrypt [true] Indica si se debe encriptar el valor almacenado. Por defecto, true.
  */
   setItem(key: string, value: any, shouldEncrypt: boolean = false, storageType: StorageType = StorageType.Local, expirationInMinutes?: number): void {
      try {
         const storage = this.getStorage(storageType);
         // Construimos el objeto a almacenar con el valor y la posible expiración.
         const item: StorageItem = { value };
         if (expirationInMinutes && expirationInMinutes > 0) {
            item.expiresAt = Date.now() + expirationInMinutes * 60 * 1000;
         }

         // Serializamos y encriptamos si corresponde
         const fullKey = this.buildKey(key);
         const serializedData = JSON.stringify(item);
         const dataToStore = shouldEncrypt
            ? this.encrypt(serializedData)
            : serializedData;

         storage.setItem(fullKey, dataToStore);

         // Envía un mensaje notificando la operación "set"
         const message: StorageSyncMessage = {
            clientId: this.clientId,
            action: 'set',
            key: fullKey,
            data: item
         };
         // this.broadcastChannel.postMessage(message);
         // Y también emitimos el mensaje de forma reactiva en esta misma pestaña
         this.storageSyncSubject.next(message);

      } catch (error) {
         console.error(`Error al guardar ${key} en ${StorageType[storageType]}Storage:`, error);
      }
   }

   /**
    * Recupera y parsea un valor del storage seleccionado.
    * Si el ítem existe pero ha expirado, lo elimina y devuelve null.
    * @param key Identificador sin prefijo.
    * @param storageType [StorageType.Local] Tipo de almacenamiento (local o session). Por defecto, local.
    * @param isEncrypted [true] Indica si el valor almacenado está encriptado. Por defecto, true.
    */
   getItem(key: string, isEncrypted: boolean = false, storageType: StorageType = StorageType.Local): any {
      try {
         const storage = this.getStorage(storageType);
         const fullKey = this.buildKey(key);
         const itemStr = storage.getItem(fullKey);

         if (!itemStr) {
            return null;
         }

         // Desencriptar si está encriptado
         const decryptedStr = isEncrypted
            ? this.decrypt(itemStr)
            : itemStr;

         const item: StorageItem = JSON.parse(decryptedStr);

         // Verificar si tiene fecha de expiración y si ya expiró
         if (item.expiresAt && Date.now() > item.expiresAt) {
            // Expirado: eliminar la key y devolver null
            this.removeItem(key, storageType);
            return null;
         }
         return item.value;
      } catch (error) {
         console.error(`Error al leer ${key} de ${StorageType[storageType]}Storage:`, error);
         return null;
      }
   }

   /**
   * Elimina un ítem del storage seleccionado.
   * @param key Identificador sin prefijo.
   */
   removeItem(key: string, storageType: StorageType = StorageType.Local): void {
      try {
         const storage = this.getStorage(storageType);
         const fullKey = this.buildKey(key);
         storage.removeItem(fullKey);

         // Envía un mensaje notificando la operación "remove"
         const message: StorageSyncMessage = {
            clientId: this.clientId,
            action: 'remove',
            key: fullKey
         };
         // this.broadcastChannel.postMessage(message);
         // Y también emitimos el mensaje de forma reactiva en esta misma pestaña
         this.storageSyncSubject.next(message);

      } catch (error) {
         console.error(`Error al remover ${key} de ${StorageType[storageType]}Storage:`, error);
      }
   }

   /**
    * Limpia el storage seleccionado.
    * Nota: Esto limpiará **todo** el storage, no solo los ítems con el prefijo de la app.
    * Podrías implementar una lógica para limpiar solo los ítems de tu aplicación si lo prefieres.
    */
   clear(storageType: StorageType = StorageType.Local): void {
      try {
         const storage = this.getStorage(storageType);
         storage.clear();

         // Envía un mensaje notificando la operación "clear"
         const message: StorageSyncMessage = {
            clientId: this.clientId,
            action: 'clear'
         };
         // this.broadcastChannel.postMessage(message);
         // Y también emitimos el mensaje de forma reactiva en esta misma pestaña
         this.storageSyncSubject.next(message);

      } catch (error) {
         console.error(`Error al limpiar ${StorageType[storageType]}Storage:`, error);
      }
   }

   /**
    * Construye la key completa añadiendo el prefijo del nombre de la aplicación.
    */
   private buildKey(key: string): string {
      return `${this.keyPrefix}_${key}`;
   }

   /**
    * Retorna la referencia al storage según el tipo especificado.
    */
   private getStorage(storageType: StorageType): Storage {
      return storageType === StorageType.Local ? localStorage : sessionStorage;
   }

}
